/**
 * Checks whether a selected File's type satisfies an input's `accept` attribute.
 *
 * Pass the attribute itself (`input.accept`), not a hand-written list. That keeps the rule
 * defined in one place — the markup the server rendered — so the client check cannot drift
 * from what the server will actually accept.
 *
 * Type only: this never looks at file size. Pair it with your own size check.
 *
 * Supports the two `accept` forms in use here — a wildcard (`image/*`) and an explicit MIME
 * list (`image/jpeg,image/png`). It does NOT support extension entries (`.jpg,.pdf`) and
 * would reject every file for an input using those, so add that branch before doing so.
 *
 * @example
 *   fileMatchesAccept(pngFile, 'image/*');              // => true
 *   fileMatchesAccept(svgFile, 'image/jpeg,image/png'); // => false
 *
 * @example
 *   // Typical Stimulus use: accept comes off the input, the size limit from a value.
 *   isValidLogo(file) {
 *     return (
 *       fileMatchesAccept(file, this.logoInputTarget.accept) &&
 *       file.size <= this.maxFileSizeValue
 *     );
 *   }
 *
 * @param {File} file
 * @param {string} accept an input's `accept` attribute value
 * @return {boolean} true when the type matches, or when the browser reported no type at all
 */
const fileMatchesAccept = (file, accept) => {
  // A file's reported MIME type can come back blank for some formats in some browsers
  // (notably HEIC/HEIC-sequence). This is a client-side pre-check for UX speed, not the real
  // security boundary — the server still validates — so treat "unknown" as passing rather
  // than falsely blocking a legitimate upload the server would accept.
  if (!file.type) {
    return true;
  }
  return accept
    .split(',')
    .map((pattern) => pattern.trim())
    .some((pattern) =>
      pattern.endsWith('/*') ? file.type.startsWith(pattern.slice(0, -1)) : file.type === pattern
    );
};

export default fileMatchesAccept;
